import { supabase } from '../lib/supabase';
import type { Status } from '../book/types';
import type { NoticeInput } from '../shell/notice';
import { allRows, loadCovers, rows } from '../shelves/load';
import type { CatalogList, ListEntry, ListKind } from './lists';

// Discover's lists (Part 3a), read with your own sign-in: the lists catalog is shared (everyone
// signed in reads it); follows and hidden lists are yours. Follow, Unfollow, Hide and Show again
// are your own rows, each with Undo in the shared notice.

type Row = Record<string, unknown>;
const one = <T>(x: unknown): T | null => (Array.isArray(x) ? ((x[0] as T) ?? null) : ((x as T) ?? null));

function db() {
  if (!supabase) throw new Error('Not connected to a database.');
  return supabase;
}

export type MyWork = { id: string; label: number; status: Status | null };

/** Your books by catalog work, for "on your list". */
export async function loadMyWorks(): Promise<Map<string, MyWork>> {
  const list = await allRows(() => db().from('books').select('id, label_number, status, work_id').is('removed_at', null).order('id') as never);
  return new Map(list.map((b) => [b.work_id as string, { id: b.id as string, label: Number(b.label_number), status: (b.status as Status) ?? null }]));
}

export interface Catalog {
  lists: CatalogList[];
  /** For each followed list, its newest year: how many books, how many on your list. */
  latest: Map<string, { year: number; total: number; mine: number }>;
}

/** The lists that have books loaded, with your follows, hidden lists and counts. */
export async function loadCatalog(): Promise<Catalog> {
  const [lists, editions, matched, follows, hidden, mine] = await Promise.all([
    rows(db().from('lists').select('id, name, kind, publisher, frequency, since_year, levels, data_source, sort_order').order('sort_order')),
    allRows(() => db().from('list_editions').select('id, list_id, year').order('id') as never),
    allRows(() => db().from('list_entries').select('id, work_id, list_edition_id').not('work_id', 'is', null).order('id') as never),
    rows(db().from('follows').select('list_id').not('list_id', 'is', null)),
    rows(db().from('hidden_lists').select('list_id')),
    loadMyWorks(),
  ]);
  const editionList = new Map(editions.map((e) => [e.id as string, e.list_id as string]));
  const followed = new Set(follows.map((f) => f.list_id as string));
  const hiddenSet = new Set(hidden.map((h) => h.list_id as string));
  const out: CatalogList[] = [];
  for (const l of lists) {
    const id = l.id as string;
    const years = [...new Set(editions.filter((e) => e.list_id === id).map((e) => Number(e.year)))].sort((a, b) => b - a);
    if (!years.length) continue; // only lists with books loaded (Kristi, Nov 3)
    const works = new Set(matched.filter((m) => editionList.get(m.list_edition_id as string) === id && mine.has(m.work_id as string)).map((m) => m.work_id as string));
    out.push({
      id,
      name: l.name as string,
      kind: l.kind as ListKind,
      publisher: (l.publisher as string) ?? null,
      frequency: (l.frequency as string) ?? null,
      since: l.since_year === null || l.since_year === undefined ? (years.at(-1) ?? null) : Number(l.since_year),
      levels: (l.levels as string[]) ?? [],
      source: (l.data_source as string) ?? null,
      years,
      onYourList: works.size,
      following: followed.has(id),
      hidden: hiddenSet.has(id),
    });
  }
  // The followed lists' newest year: its size and how many of its books you have.
  const latest = new Map<string, { year: number; total: number; mine: number }>();
  await Promise.all(
    out
      .filter((l) => l.following && !l.hidden)
      .map(async (l) => {
        const year = l.years[0]!;
        const eds = editions.filter((e) => e.list_id === l.id && Number(e.year) === year).map((e) => e.id as string);
        const { count, error } = await db().from('list_entries').select('id', { count: 'exact', head: true }).in('list_edition_id', eds);
        if (error) throw new Error(error.message);
        const on = new Set(matched.filter((m) => eds.includes(m.list_edition_id as string) && mine.has(m.work_id as string)).map((m) => m.work_id as string)).size;
        latest.set(l.id, { year, total: count ?? 0, mine: on });
      }),
  );
  return { lists: out, latest };
}

export interface ListPageData {
  list: CatalogList;
  year: number;
  /** Several editions in one year (the Lost Man Booker): each label, in order. */
  labels: (string | null)[];
  source: string | null;
  collectedOn: string | null;
  entries: ListEntry[];
}

/** One list's year: its entries with covers and which are on your list. */
export async function loadListPage(listId: string, year: number | null): Promise<ListPageData | null> {
  const cat = await loadCatalog();
  const list = cat.lists.find((l) => l.id === listId);
  if (!list) return null;
  const y = year && list.years.includes(year) ? year : list.years[0]!;
  const [editions, mine] = await Promise.all([
    rows(db().from('list_editions').select('id, label, source_url, collected_on').eq('list_id', listId).eq('year', y).order('label', { nullsFirst: true })),
    loadMyWorks(),
  ]);
  const ids = editions.map((e) => e.id as string);
  const raw = ids.length
    ? await allRows(() =>
        db()
          .from('list_entries')
          .select('id, rank, level, category, printed_title, printed_author, work_id, work:works(title, fiction)')
          .in('list_edition_id', ids)
          .order('id') as never,
      )
    : [];
  const works = [...new Set(raw.map((r) => r.work_id as string).filter(Boolean))];
  const covers = works.length ? await loadCovers(works.map((w) => ({ id: w, workId: w }))) : new Map<string, string | null>();
  const entries: ListEntry[] = raw.map((r) => {
    const w = one<Row>(r.work);
    const workId = (r.work_id as string) ?? null;
    return {
      id: r.id as string,
      rank: r.rank === null || r.rank === undefined ? null : Number(r.rank),
      level: (r.level as string) ?? null,
      category: (r.category as string) ?? null,
      title: (r.printed_title as string) ?? (w?.title as string) ?? '',
      author: (r.printed_author as string) ?? '',
      workId,
      literary: (w?.fiction as string) ?? null,
      cover: workId ? (covers.get(workId) ?? null) : null,
      mine: workId ? (mine.get(workId) ?? null) : null,
    };
  });
  const first = editions[0];
  return {
    list,
    year: y,
    labels: editions.map((e) => (e.label as string) ?? null),
    source: (first?.source_url as string) ?? null,
    collectedOn: (first?.collected_on as string) ?? null,
    entries,
  };
}

// ---------- your choices, each with Undo ----------

type Notice = (n: NoticeInput) => void;
const fail = (notice: Notice, what: string, e: unknown) => notice({ message: `${what}: ${e instanceof Error ? e.message : String(e)}`, done: false });

async function setFollow(list: string, on: boolean) {
  const q = on ? db().from('follows').insert({ list_id: list }) : db().from('follows').delete().eq('list_id', list);
  const { error } = await q;
  if (error && !(on && error.code === '23505')) throw new Error(error.message);
}

async function setHidden(list: string, on: boolean) {
  const q = on ? db().from('hidden_lists').insert({ list_id: list }) : db().from('hidden_lists').delete().eq('list_id', list);
  const { error } = await q;
  if (error && !(on && error.code === '23505')) throw new Error(error.message);
}

/** Follow or unfollow a list. */
export async function followList(notice: Notice, list: Pick<CatalogList, 'id' | 'name'>, on: boolean, done: () => void): Promise<void> {
  try {
    await setFollow(list.id, on);
    done();
    notice({
      message: on ? `Following ${list.name}` : `Stopped following ${list.name}`,
      undo: async () => {
        try {
          await setFollow(list.id, !on);
          done();
          notice({ message: `Undone: ${list.name} is as it was` });
        } catch (e) {
          fail(notice, 'Couldn’t undo', e);
        }
      },
    });
  } catch (e) {
    fail(notice, on ? 'Not followed' : 'Still following', e);
  }
}

/** Hide a list (it leaves the catalog and is unfollowed), or show it again. */
export async function hideList(notice: Notice, list: Pick<CatalogList, 'id' | 'name' | 'following'>, on: boolean, done: () => void): Promise<void> {
  const wasFollowing = list.following;
  try {
    await setHidden(list.id, on);
    if (on && wasFollowing) await setFollow(list.id, false);
    done();
    notice({
      message: on ? `${list.name} hidden` : `${list.name} is back in the catalog`,
      undo: async () => {
        try {
          await setHidden(list.id, !on);
          if (on && wasFollowing) await setFollow(list.id, true);
          done();
          notice({ message: `Undone: ${list.name} is as it was` });
        } catch (e) {
          fail(notice, 'Couldn’t undo', e);
        }
      },
    });
  } catch (e) {
    fail(notice, on ? 'Not hidden' : 'Still hidden', e);
  }
}
